import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import type { GridColDef, GridRowDef } from '@p81-common/ui';
import { GridIsHaCell } from '../parts/custom-cells/GridIsHaCell';

import { createReqBody } from '../helpers/helpers';
import {
  checkingForExistenceOfOptions,
  clearOrDefaultFilters,
  parsConfigForHistory,
  replaceHistoryCML,
  reParsConfigForHistory
} from 'utils/common';
import { getRequest, postRequest } from 'utils/requests';

import { useDataGrid } from 'hooks/useDataGrid';
import {
  GRID_DETAIL_PANEL_FIELD,
  defaultFilters,
  defaultNavigation,
  NetworkRolloutFilter
} from './config';

const FILTERS_URL_PREFIX = '/network_rollout/list/filters';
const REQUEST_FOR_FILTERED_DATA_DELAY = 200;

export const useNetworkRolloutDataGrid = (tableConfiguration: GridColDef[]) => {
  const history = useHistory();
  const isInitialized = useRef(false);
  const unmount = useRef(false);

  const useDataGridResultProps = useDataGrid(
    tableConfiguration,
    defaultFilters
  );

  const {
    setFilter,
    filter,
    setResults,
    setTableData,
    tableConfigDisplay,
    setLoaderTable,
    direction,
    startFilter,
    setStartFilter,
    setVisibleFilterSave,
    setVisibleFilterEdit,
    changeFilter,
    setChangeFilter,
    trigerResetDatePicker,
    setTrigerResetDatePicker,
    tableConf,
    setTableConf,
    filters,
    setFilters,
    setLoader,
    setDataConection,
    setSelectLoader,
    setPrevStateFilter,
    setPrevStateTableConf
  } = useDataGridResultProps;

  useEffect(() => {
    if (!isInitialized.current) return;

    replaceHistoryCML(
      direction,
      filter,
      undefined,
      history,
      false,
      false,
      changeFilter,
      parsConfigForHistory(tableConf)
    );

    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    setLoaderTable(true);
    const query = new URLSearchParams(history.location.search);
    const queryFilter = query.get('filter') || '';
    const queryTableConf = query.get('tableConf') || '';
    if (!queryFilter) {
      const isDefaultFilter = isEqual(filter, {
        ...defaultFilters,
        ...defaultNavigation
      });
      if (isDefaultFilter) {
        replaceHistoryCML(
          false,
          filter,
          undefined,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        delayedRequestForFilteredData();
      } else {
        resetAllfilters(true);
        replaceHistoryCML(
          false,
          filter,
          undefined,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      }
    } else {
      let parsedFilter;
      try {
        parsedFilter = JSON.parse(queryFilter);
      } catch (err) {
        !unmount.current && console.log(err);
      }
      setFilter(parsedFilter);
      delayedRequestForFilteredData(parsedFilter);

      if (queryTableConf) {
        setPrevStateTableConf(
          reParsConfigForHistory(JSON.parse(queryTableConf))
        );
        setTableConf(
          reParsConfigForHistory(JSON.parse(queryTableConf), tableConfigDisplay)
        );
      } else {
        setPrevStateTableConf(tableConf);
        setTableConf(tableConfigDisplay);
      }
    }
    setTrigerResetDatePicker(!trigerResetDatePicker);
    isInitialized.current = true;

    // eslint-disable-next-line
  }, [history.location.search]);

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = (listFilter: typeof filter) => {
    setSelectLoader(true);
    getRequest(FILTERS_URL_PREFIX)
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({
            ...filters,
            ...body
          });

          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          if (newCheckingList) {
            setPrevStateFilter(newCheckingList);
            replaceHistoryCML(
              '',
              filter,
              undefined,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );
          }
          setSelectLoader(false);
        }
      })
      .catch(err => {
        !unmount.current && console.log(err);
      });
  };

  const requestForFilteredData = (
    listFilter = filter as NetworkRolloutFilterType
  ) => {
    const linkForRequest = `network_rollout/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;

    const REQ_BODY = createReqBody(listFilter);

    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          const formattedData = res?.body?.data?.map((record: any) => {
            const updatedRecords = Array.from(
              new Set(
                record?.gateWays?.map(
                  (gw: Record<string, string>) => gw.providerRegionName
                )
              )
            );
            return { ...record, updatedRecords };
          });
          setTableData(formattedData);
          setResults(res.body);
          setDataConection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };
  const delayedRequestForFilteredData = debounce(
    requestForFilteredData,
    REQUEST_FOR_FILTERED_DATA_DELAY
  );

  const resetAllfilters = (
    reload?: boolean,
    onlyFilterLabel?: boolean,
    onClickDefault?: boolean,
    modeClearAll?: boolean
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(tableConfigDisplay)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(tableConfigDisplay)));
    }

    setTrigerResetDatePicker(!trigerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  const transformGridColData: (config: GridColDef) => GridColDef = config =>
    config.field === 'isHa'
      ? {
          ...config,
          renderCell: props => <GridIsHaCell {...props} />
        }
      : config;

  const transformGridRowData = (row: GridRowDef) => ({
    ...row,
    gwCount: (row?.[GRID_DETAIL_PANEL_FIELD] as unknown[])?.length || undefined,
    regionName: row.country,
    regionCode: row.country as string,
    pricingPlan: row.plan,
    billingPlan: row.billingCycle,
    accManager: {
      fullName: row.accountManager,
      userEmail: row.updatedByEmail as string
    },
    csEngineer: {
      fullName: row.customerSuccessEngineer
    },
    // prepare region data for DataGrid's GridTextGroupCell
    gateWays: (row.gateWays as GridRowDef[])?.map(gateway => ({
      ...gateway,
      regionCode: gateway.regionCountry,
      regionName: gateway.providerRegionName
    }))
  });

  type NetworkRolloutFilterType = typeof filter & NetworkRolloutFilter;

  return {
    ...useDataGridResultProps,
    filter: filter as NetworkRolloutFilterType,
    filters: filters as NetworkRolloutFilterType,
    resetAllfilters,
    transformGridColData,
    transformGridRowData
  };
};
